import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function ConsumerAuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState('authenticating');
  const hasRun = React.useRef(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- Intentionally runs once on mount (hasRun ref guard)
  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    const authenticate = async () => {
      try {
        const urlParams = new URLSearchParams(location.search);
        const token = urlParams.get('token');
        const tokenType = urlParams.get('stytch_token_type');

        if (!token || !tokenType) {
          setStatus('error');
          return;
        }

        const response = await axios.post(
          `${API_BASE_URL}/api/consumer/auth/magic-link/authenticate`,
          { token, token_type: tokenType }
        );

        // Save session
        sessionStorage.setItem('consumer_session_token', response.data.session_token);
        sessionStorage.setItem('consumer_user', JSON.stringify(response.data.user));

        setStatus('success');
        
        toast.success('Welcome to Commerce Index!');

        setTimeout(() => {
          navigate('/deals', { replace: true });
        }, 1500);

      } catch (error) {
        console.error('Authentication error:', error);
        setStatus('error');
        toast.error('Authentication failed', {
          description: 'Please try signing in again'
        });
      }
    };

    authenticate();
  }, [location, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-blue-100 to-blue-200 flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8 w-full max-w-md text-center">
        {status === 'authenticating' && (
          <>
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-blue-600 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Verifying your link...</h2>
            <p className="text-sm text-gray-600">Please wait</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Success!</h2>
            <p className="text-sm text-gray-600">Redirecting to deals...</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Authentication failed</h2>
            <p className="text-sm text-gray-600 mb-6">The link may have expired or is invalid</p>
            <button
              onClick={() => navigate('/auth')}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
            >
              Try Again
            </button>
          </>
        )}
      </div>
    </div>
  );
}
